import { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { colors } from "../../styles/theme";
import { Button } from "../../components/ui";
import { runDataCleanup } from "../../services/api";

export default function DataCleanupSection() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const handlePreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const data = await runDataCleanup("preview");
      setPreview(data);
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!window.confirm("This will update contacts across the database. Continue?")) return;
    setApplying(true);
    setResult(null);
    try {
      const data = await runDataCleanup("apply");
      setResult(data);
      setPreview(null);
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setApplying(false);
    }
  };

  const statRow = (label, value, color) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${colors.border}` }}>
      <span style={{ fontSize: 13, color: colors.textSecondary }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: color || colors.text }}>{value}</span>
    </div>
  );

  const data = preview || result;

  return (
    <div style={{ background: colors.surface, borderRadius: 12, padding: 24, border: `1px solid ${colors.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <RefreshCw size={18} color={colors.accent} />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.text }}>Data Cleanup</h3>
      </div>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: "0 0 16px", lineHeight: 1.5 }}>
        Retroactively apply company tags and sectors to existing contacts. Also auto-links contacts to companies by email domain where possible.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Button onClick={handlePreview} disabled={loading || applying} style={{ fontSize: 13 }}>
          {loading ? <><Loader2 size={14} className="spin" /> Scanning...</> : "Preview Changes"}
        </Button>
        {preview && !preview.error && (
          <Button onClick={handleApply} disabled={applying} style={{ fontSize: 13, background: colors.accent, color: "#fff" }}>
            {applying ? <><Loader2 size={14} className="spin" /> Applying...</> : "Apply Changes"}
          </Button>
        )}
      </div>

      {data && !data.error && (
        <div style={{ background: colors.bg, borderRadius: 8, padding: 16, border: `1px solid ${colors.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", color: colors.textSecondary, marginBottom: 8 }}>
            {data.dryRun ? "Preview" : "Applied"}
          </div>
          {statRow("Total contacts scanned", data.totalContacts)}
          {statRow("Already linked to company", data.linkedToCompany)}
          {statRow("Newly linked by domain", data.newlyLinked, data.newlyLinked > 0 ? "#22c55e" : undefined)}
          {statRow("Tags updated", data.tagsUpdated, data.tagsUpdated > 0 ? "#22c55e" : undefined)}
          {statRow("Sectors updated", data.sectorUpdated, data.sectorUpdated > 0 ? "#22c55e" : undefined)}
          {statRow("Free email (skipped)", data.freeEmailSkipped, colors.textSecondary)}
          {statRow("Orphaned (no company match)", data.orphaned, data.orphaned > 0 ? "#f59e0b" : undefined)}
          {data.errors?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#ef4444", marginBottom: 4 }}>Errors ({data.errors.length})</div>
              {data.errors.slice(0, 5).map((e, i) => (
                <div key={i} style={{ fontSize: 12, color: colors.textSecondary }}>{e.email}: {e.error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {data?.error && (
        <div style={{ padding: 12, background: "#fef2f2", borderRadius: 8, color: "#dc2626", fontSize: 13 }}>
          {data.error}
        </div>
      )}

      {result && !result.error && !result.dryRun && (
        <div style={{ marginTop: 12, padding: 12, background: "#f0fdf4", borderRadius: 8, color: "#16a34a", fontSize: 13 }}>
          Cleanup complete. {result.newlyLinked} contacts linked, {result.tagsUpdated} tags updated, {result.sectorUpdated} sectors updated.
        </div>
      )}
    </div>
  );
}
